import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";

import { Translation as T, useAmsSelector } from "@awe-web/shared/lib";
import { provisioningTypes } from "@awe-web/shared/lib";
import {
  newIsFieldRequired,
  newGetFieldLabel,
  newGetFieldPlaceholder,
  newGetSelectFieldOptions
} from "@awe-web/shared/lib";

import { FormikLabeledField, FormikTextInput, FormikSelect } from "../../../components/form.js";
import { ProvisioningSelection } from "../../../rent_contracts/components/provisioning_selection.js";
import { StationAddress } from "../../../stations/components/station_card";
import { getFormattedDeliveryPrice, getFormattedPickupPrice, getIsDeliveryEnabled } from "../../../rent_contracts/store.js";
import { getCoStation } from "../../../stations/store.js";
import { getCompanyAddress } from "../../../user/store.js";

const OPTION_PICKUP = "ALG0000070";
const OPTION_DELIVERY_TO_COMPANY_ADDRESS = "ALG0000068";
const OPTION_DELIVERY_TO_CUSTOM_ADDRESS = "ALG0000069";

export default function SelectProvisioningForm({ values: { provisioningType } }) {
  const formik = useFormikContext();

  // Store formik in ref to avoid being forced to put it in the dependency
  // array which runs the effect below more often than it needs to.
  const formikRef = useRef();
  formikRef.current = formik;

  const stateCompanyAddress = useSelector((state) => getCompanyAddress(state));
  const companyAddress = useMemo(() => stateCompanyAddress.toJS(), [stateCompanyAddress]);

  const coStation = useSelector((state) => getCoStation(state));
  const formattedDeliveryPrice = useSelector((state) => getFormattedDeliveryPrice(state));
  const formattedPickupPrice = useSelector((state) => getFormattedPickupPrice(state));
  const isDeliveryEnabled = useSelector((state) => getIsDeliveryEnabled(state));

  const isDeliveryFieldRequired = useAmsSelector((state) => newIsFieldRequired("deliveryAddress07")(state));
  const getDeliveryFieldLabel = useAmsSelector((state) => newGetFieldLabel("deliveryAddress07")(state));
  const getDeliveryFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("deliveryAddress07")(state));
  const getDeliverySelectFieldOptions = useAmsSelector((state) => newGetSelectFieldOptions("deliveryAddress07")(state));

  // Untouch fields in delivery form every time it is opened in order to remove
  // previous error messages.
  useEffect(() => {
    if (provisioningType === provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS) {
      [
        "DeliveryAddress:surname",
        "DeliveryContactPerson:name",
        "DeliveryContactPerson:surname",
        "DeliveryAddress:street",
        "DeliveryAddress:building",
        "DeliveryAddress:zipCode",
        "DeliveryAddress:city",
        "DeliveryAddress:countryCode",
        "DeliveryContactPerson:phone"
      ]
        .filter((name) => formikRef.current.touched[name])
        .forEach((name) => {
          formikRef.current.setFieldTouched(name, false);
        });
    }
  }, [provisioningType]);

  const populateDeliveryFormRef = useRef(provisioningType !== provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS);

  useEffect(() => {
    if (populateDeliveryFormRef.current && provisioningType === provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS) {
      populateDeliveryFormRef.current = false;
      formikRef.current.setValues({
        ...formikRef.current.values,
        "DeliveryContactPerson:name": formikRef.current.values["Person:name"],
        "DeliveryContactPerson:surname": formikRef.current.values["Person:surname"],
        "DeliveryContactPerson:phone": formikRef.current.values["Address:phone"]
      });
    }
  }, [provisioningType]);

  return (
    <div className="select-provisioning-form b2b-ltr-select-provisioning-form__provisioning">
      <ProvisioningSelection
        name="provisioningType"
        value={provisioningTypes.PICKUP}
        label={<T code={OPTION_PICKUP} />}
        formattedPrice={formattedPickupPrice}
      >
        <StationAddress
          className="b2b-ltr-select-provisioning-form__station-address"
          stationNameClassName="b2b-ltr-select-provisioning-form__station-address__name"
          openingTimesHeadingClassName="b2b-ltr-select-provisioning-form__station-address__opening-times-heading"
          station={coStation?.toJS()}
        />
      </ProvisioningSelection>

      <ProvisioningSelection
        name="provisioningType"
        value={provisioningTypes.DELIVERY_TO_COMPANY_ADDRESS}
        label={<T code={OPTION_DELIVERY_TO_COMPANY_ADDRESS} />}
        formattedPrice={formattedDeliveryPrice}
        disabled={!isDeliveryEnabled}
      >
        <div className="b2b-ltr-select-provisioning-form__company-address">
          {companyAddress.companyName}<br />
          {companyAddress.street}<br />
          {companyAddress.building
            ? <>{companyAddress.building}<br /></>
            : null
          }
          {companyAddress.zipCode} {companyAddress.city}<br />
          {companyAddress.country}
        </div>
      </ProvisioningSelection>

      <ProvisioningSelection
        name="provisioningType"
        value={provisioningTypes.DELIVERY_TO_CUSTOM_ADDRESS}
        label={<T code={OPTION_DELIVERY_TO_CUSTOM_ADDRESS} />}
        formattedPrice={formattedDeliveryPrice}
        disabled={!isDeliveryEnabled}
      >
        <div className="b2b-ltr-select-provisioning-form__fieldset">
          <FormikLabeledField
            name="DeliveryAddress:surname"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:surname")}
          />

          <FormikLabeledField
            name="DeliveryContactPerson:name"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:name")}
          />

          <FormikLabeledField
            name="DeliveryContactPerson:surname"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:surname")}
          />

          <FormikLabeledField
            name="DeliveryAddress:street"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:street")}
          />

          <FormikLabeledField
            name="DeliveryAddress:building"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:building")}
          />

          <div className="b2b-ltr-select-provisioning-form__zip-code-and-city">
            <FormikLabeledField
              className="b2b-ltr-select-provisioning-form__zip-code-and-city__zip-code"
              name="DeliveryAddress:zipCode"
              as={FormikTextInput}
              isFieldRequired={isDeliveryFieldRequired}
              getFieldLabel={getDeliveryFieldLabel}
              placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:zipCode")}
            />

            <FormikLabeledField
              className="b2b-ltr-select-provisioning-form__zip-code-and-city__city"
              name="DeliveryAddress:city"
              as={FormikTextInput}
              isFieldRequired={isDeliveryFieldRequired}
              getFieldLabel={getDeliveryFieldLabel}
              placeholder={getDeliveryFieldPlaceholder("DeliveryAddress:city")}
            />
          </div>

          <FormikLabeledField
            name="DeliveryAddress:countryCode"
            as={FormikSelect}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            options={getDeliverySelectFieldOptions("DeliveryAddress:countryCode")}
          />

          <FormikLabeledField
            name="DeliveryContactPerson:phone"
            as={FormikTextInput}
            isFieldRequired={isDeliveryFieldRequired}
            getFieldLabel={getDeliveryFieldLabel}
            placeholder={getDeliveryFieldPlaceholder("DeliveryContactPerson:phone")}
          />
        </div>
      </ProvisioningSelection>
    </div>
  );
};
SelectProvisioningForm.propTypes = {
  values: PropTypes.shape({
    provisioningType: PropTypes.oneOf(Object.values(provisioningTypes))
  }).isRequired,
};
